import types from '../../mutation-types';
import ConversationApi from '../../../api/inbox/conversation';
import MessageApi from '../../../api/inbox/message';
import { MESSAGE_STATUS, MESSAGE_TYPE } from 'shared/constants/messages';
import { createPendingMessage } from 'dashboard/helper/commons';
import { useAlert } from 'dashboard/composables';
import {
  buildConversationList,
  isOnMentionsView,
  isOnUnattendedView,
  isOnRepliedByMeView,
  isOnFoldersView,
  isOnCommentsView,
} from './helpers/actionHelpers';
import { matchesFilters } from './helpers/filterHelpers';
import messageReadActions from './actions/messageReadActions';
import messageTranslateActions from './actions/messageTranslateActions';
import * as Sentry from '@sentry/vue';
import {
  handleVoiceCallCreated,
  handleVoiceCallUpdated,
} from 'dashboard/helper/voice';

export const hasMessageFailedWithExternalError = pendingMessage => {
  // This helper is used to check if the message has failed with an external error.
  // We have two cases
  // 1. Messages that fail from the UI itself (due to large attachments or a failed network):
  //    In this case, the message will have a status of failed but no external error. So we need to create that message again
  // 2. Messages sent from Chatwoot but failed to deliver to the customer for some reason (user blocking or client system down):
  //    In this case, the message will have a status of failed and an external error. So we need to retry that message
  const { content_attributes: contentAttributes, status } = pendingMessage;
  const externalError = contentAttributes?.external_error ?? '';
  return status === MESSAGE_STATUS.FAILED && externalError !== '';
};

// actions
const actions = {
  getConversation: async ({ commit }, conversationId) => {
    try {
      const response = await ConversationApi.show(conversationId);
      commit(types.UPDATE_CONVERSATION, response.data);
      commit(`contacts/${types.SET_CONTACT_ITEM}`, response.data.meta.sender);
    } catch (error) {
      // Ignore error
    }
  },

  fetchAllConversations: async ({ commit, state, dispatch }) => {
    commit(types.SET_LIST_LOADING_STATUS);
    try {
      const params = state.conversationFilters;
      const {
        data: { data },
      } = await ConversationApi.get(params);
      buildConversationList(
        { commit, dispatch },
        params,
        data,
        params.assigneeType
      );
    } catch (error) {
      // Eltafouk: always clear the loading flag on failure — otherwise
      // the chat list spinner ("جاري جلب المحادثات") stays on screen
      // forever and the user never sees the empty state to retry.
      commit(types.CLEAR_LIST_LOADING_STATUS);
    }
  },

  fetchFilteredConversations: async ({ commit, dispatch }, params) => {
    commit(types.SET_LIST_LOADING_STATUS);
    try {
      // Eltafouk: forward perPage + conversationType so the filter
      // endpoint can honour them the same way the index endpoint does
      // when the "غير مقروء" pill is on (large page + unread overlay).
      const { data } = await ConversationApi.filter({
        queryData: params.queryData,
        page: params.page,
        perPage: params.perPage,
        conversationType: params.conversationType,
      });
      buildConversationList(
        { commit, dispatch },
        params,
        data,
        'appliedFilters'
      );
    } catch (error) {
      commit(types.CLEAR_LIST_LOADING_STATUS);
      // Eltafouk: a rejected filter (e.g. a condition applied with no value, which
      // the API answers with 422 «Invalid value…») used to be swallowed here — the
      // list simply stayed empty under a header still showing the previous count,
      // which reads as "the conversations disappeared". Say what happened instead.
      useAlert(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          'تعذّر تطبيق التصفية — راجع الشروط وحاول تاني.'
      );
    }
  },

  emptyAllConversations({ commit }) {
    commit(types.EMPTY_ALL_CONVERSATION);
  },

  clearSelectedState({ commit }) {
    commit(types.CLEAR_CURRENT_CHAT_WINDOW);
  },

  fetchPreviousMessages: async ({ commit }, data) => {
    try {
      const {
        data: { meta, payload },
      } = await MessageApi.getPreviousMessages(data);
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: data.conversationId,
        data: meta,
      });
      commit(types.SET_PREVIOUS_CONVERSATIONS, {
        id: data.conversationId,
        data: payload,
      });
      if (!payload.length) {
        commit(types.SET_ALL_MESSAGES_LOADED, data.conversationId);
      }
    } catch (error) {
      // Handle error
    }
  },

  fetchAllAttachments: async ({ commit }, conversationId) => {
    let attachments = [];

    try {
      const { data } = await ConversationApi.getAllAttachments(conversationId);
      attachments = data.payload;
    } catch (error) {
      // in case of error, log the error and continue
      Sentry.setContext('Conversation', {
        id: conversationId,
      });
      Sentry.captureException(error);
    } finally {
      // we run the commit even if the request fails
      // this ensures that the `attachment` variable is always present on chat
      commit(types.SET_ALL_ATTACHMENTS, {
        id: conversationId,
        data: attachments,
      });
    }
  },

  syncActiveConversationMessages: async (
    { commit, state, dispatch },
    { conversationId }
  ) => {
    const { allConversations, syncConversationsMessages } = state;
    const lastMessageId = syncConversationsMessages[conversationId];
    const selectedChat = allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    try {
      const { messages } = selectedChat;
      // Fetch all the messages after the last message id
      const {
        data: { meta, payload },
      } = await MessageApi.getPreviousMessages({
        conversationId,
        after: lastMessageId,
      });
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: conversationId,
        data: meta,
      });
      // Find the messages that are not already present in the store
      const missingMessages = payload.filter(
        message => !messages.find(item => item.id === message.id)
      );
      if (missingMessages.length) {
        // Build a new sorted array so Vue detects a reference change and
        // re-renders correctly. In-place sort on the same reactive array
        // returns the same reference, which Vue 3 treats as a no-op in
        // SET_MISSING_MESSAGES and may leave the UI showing the unsorted order.
        commit(types.SET_MISSING_MESSAGES, {
          id: conversationId,
          data: [...selectedChat.messages, ...missingMessages].sort(
            (a, b) => a.created_at - b.created_at
          ),
        });
      }
      commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
        conversationId,
        messageId: null,
      });
      dispatch('markMessagesRead', { id: conversationId }, { root: true });
    } catch (error) {
      // Handle error
    }
  },

  setConversationLastMessageId: async (
    { commit, state },
    { conversationId }
  ) => {
    const { allConversations } = state;
    const selectedChat = allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    const { messages } = selectedChat;
    if (!messages.length) return;
    // Use the highest message id as the reconnect cursor, not the last array
    // element. messages.last() returns the tail position, which can be a low
    // id if the array is momentarily out of order; a low cursor makes the
    // reconnect sync (messages_after: id > cursor) over-fetch and re-inject
    // old messages into the conversation.
    const maxMessageId = messages.reduce(
      (max, message) => (message.id > max ? message.id : max),
      messages[0].id
    );
    commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
      conversationId,
      messageId: maxMessageId,
    });
  },

  async setActiveChat({ commit, dispatch, rootGetters }, { data, after }) {
    commit(types.SET_CURRENT_CHAT_WINDOW, data);
    commit(types.CLEAR_ALL_MESSAGES_LOADED, data.id);
    // Eltafouk: the conversation-list payload carries a SLIM sender without
    // additional_attributes (where the ERP context — erp_customer / link_status /
    // classification — lives). Opening a chat that's already in the list never
    // refetched the full contact, so the ERP header bar stayed hidden until a
    // full page refresh. Hydrate the full contact once, only when the store still
    // holds the slim record (additional_attributes absent → never fully loaded).
    const senderId = data.meta?.sender?.id;
    if (
      senderId &&
      rootGetters['contacts/getContact'](senderId)?.additional_attributes ===
        undefined
    ) {
      dispatch('contacts/show', { id: senderId });
    }
    if (data.dataFetched === undefined) {
      try {
        await dispatch('fetchPreviousMessages', {
          after,
          before: data.messages[0].id,
          conversationId: data.id,
        });
        commit(types.SET_CHAT_DATA_FETCHED, data.id);
      } catch (error) {
        // Ignore error
      }
    }
  },

  assignAgent: async ({ dispatch }, { conversationId, agentId }) => {
    try {
      const response = await ConversationApi.assignAgent({
        conversationId,
        agentId,
      });
      dispatch('setCurrentChatAssignee', {
        conversationId,
        assignee: response.data,
      });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatAssignee({ commit }, { conversationId, assignee }) {
    commit(types.ASSIGN_AGENT, { conversationId, assignee });
  },

  assignTeam: async ({ dispatch }, { conversationId, teamId }) => {
    try {
      const response = await ConversationApi.assignTeam({
        conversationId,
        teamId,
      });
      dispatch('setCurrentChatTeam', { team: response.data, conversationId });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatTeam({ commit }, { team, conversationId }) {
    commit(types.ASSIGN_TEAM, { team, conversationId });
  },

  toggleStatus: async (
    { commit },
    {
      conversationId,
      status,
      snoozedUntil = null,
      customAttributes = null,
      closeDisposition = null,
    }
  ) => {
    try {
      // Update custom attributes first if provided
      if (customAttributes) {
        await ConversationApi.updateCustomAttributes({
          conversationId,
          customAttributes,
        });
        commit(types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES, {
          conversationId,
          customAttributes,
        });
      }

      const {
        data: {
          payload: {
            current_status: updatedStatus,
            snoozed_until: updatedSnoozedUntil,
          } = {},
        } = {},
      } = await ConversationApi.toggleStatus({
        conversationId,
        status,
        snoozedUntil,
        closeDisposition,
      });
      commit(types.CHANGE_CONVERSATION_STATUS, {
        conversationId,
        status: updatedStatus,
        snoozedUntil: updatedSnoozedUntil,
      });
    } catch (error) {
      // Handle error
    }
  },

  createPendingMessageAndSend: async ({ dispatch }, data) => {
    const pendingMessage = createPendingMessage(data);
    dispatch('sendMessageWithData', pendingMessage);
  },

  sendMessageWithData: async ({ commit }, pendingMessage) => {
    const { conversation_id: conversationId, id } = pendingMessage;
    try {
      commit(types.ADD_MESSAGE, {
        ...pendingMessage,
        status: MESSAGE_STATUS.PROGRESS,
      });
      const response = hasMessageFailedWithExternalError(pendingMessage)
        ? await MessageApi.retry(conversationId, id)
        : await MessageApi.create(pendingMessage);
      commit(types.ADD_MESSAGE, {
        ...response.data,
        status: MESSAGE_STATUS.SENT,
      });
      commit(types.ADD_CONVERSATION_ATTACHMENTS, {
        ...response.data,
        status: MESSAGE_STATUS.SENT,
      });
    } catch (error) {
      const errorMessage = error.response
        ? error.response.data.error
        : undefined;
      commit(types.ADD_MESSAGE, {
        ...pendingMessage,
        meta: {
          error: errorMessage,
        },
        status: MESSAGE_STATUS.FAILED,
      });
      throw error;
    }
  },

  addMessage({ commit, rootGetters }, message) {
    commit(types.ADD_MESSAGE, message);
    if (message.message_type === MESSAGE_TYPE.INCOMING) {
      commit(types.SET_CONVERSATION_CAN_REPLY, {
        conversationId: message.conversation_id,
        canReply: true,
      });
      commit(types.ADD_CONVERSATION_ATTACHMENTS, message);
    }
    handleVoiceCallCreated(message, rootGetters?.getCurrentUserID);
  },

  updateMessage({ commit, rootGetters }, message) {
    commit(types.ADD_MESSAGE, message);
    handleVoiceCallUpdated(commit, message, rootGetters?.getCurrentUserID);
  },

  deleteMessage: async function deleteLabels(
    { commit },
    { conversationId, messageId }
  ) {
    try {
      const { data } = await MessageApi.delete(conversationId, messageId);
      commit(types.ADD_MESSAGE, data);
      commit(types.DELETE_CONVERSATION_ATTACHMENTS, data);
    } catch (error) {
      throw new Error(error);
    }
  },

  deleteConversation: async ({ commit, dispatch, state }, conversationId) => {
    try {
      await ConversationApi.delete(conversationId);
      commit(types.DELETE_CONVERSATION, conversationId);
      // Refresh the tab counters against the list's current scope (inbox /
      // team / label / mention) — empty params would refetch whole-inbox
      // totals and clobber a scoped view's counts.
      dispatch('conversationStats/get', state?.conversationFilters || {}, {
        root: true,
      });
    } catch (error) {
      throw new Error(error);
    }
  },

  addConversation(
    { commit, state, dispatch, rootState, rootGetters },
    conversation
  ) {
    const { currentInbox, appliedFilters } = state;
    const {
      inbox_id: inboxId,
      meta: { sender },
    } = conversation;
    const hasAppliedFilters = !!appliedFilters.length;
    const isMatchingInboxFilter =
      !currentInbox || Number(currentInbox) === inboxId;
    // The all-comments view is server-filtered to comment inboxes, so a generic
    // new-conversation event for a NON-comment (DM) inbox must not be injected —
    // that's exactly what leaked a Messenger DM into «جميع قنوات التعليقات».
    const inbox = rootGetters['inboxes/getInbox'](inboxId);
    const isCommentInbox = Boolean(inbox && inbox.is_comment_inbox);
    // Inboxes flagged `hide_from_all_conversations` in Inbox Settings (the SMS
    // lines) belong only to their own inbox list — a new-conversation event must
    // not inject them into the aggregate views the backend already excludes them
    // from. `currentInbox` set to that inbox means the agent opened it explicitly.
    const isHiddenInbox =
      Boolean(inbox && inbox.hide_from_all_conversations) &&
      Number(currentInbox) !== inboxId;
    // mentions / unattended / replied-by-me are server-filtered views: the
    // store holds only the rows that belong to them, so a generic
    // new-conversation event must not inject a row the view didn't ask for —
    // otherwise unrelated conversations (e.g. bot/AI auto-replies on fresh
    // DMs) leak in real-time. Mirrors the guard in `updateConversation`. The
    // inbox guard keeps rows from other inboxes out of an inbox-scoped list.
    if (
      isOnMentionsView(rootState) ||
      isOnUnattendedView(rootState) ||
      isOnRepliedByMeView(rootState) ||
      (isOnCommentsView(rootState) && !isCommentInbox) ||
      isHiddenInbox ||
      !isMatchingInboxFilter
    ) {
      return;
    }

    // For applied-filter and saved-folder views the new conversation is only
    // surfaced if it genuinely satisfies the active query — reusing the SAME
    // `matchesFilters` the render layer (getters / ChatList) applies — so a
    // live insert never leaks a non-matching row into a filtered list.
    if (hasAppliedFilters && !matchesFilters(conversation, appliedFilters)) {
      return;
    }

    if (isOnFoldersView(rootState)) {
      // The active folder's saved query lives in the customViews module. If we
      // can't read it (e.g. it isn't set yet), stay conservative and skip —
      // injecting an unfiltered row would leak into the folder list.
      const folderQuery =
        rootState.customViews?.activeConversationFolder?.query?.payload;
      if (!folderQuery || !matchesFilters(conversation, folderQuery)) {
        return;
      }
    }

    commit(types.ADD_CONVERSATION, conversation);
    dispatch('contacts/setContact', sender);
  },

  addMentions({ dispatch, rootState }, conversation) {
    // The `conversation.mentioned` event is broadcast only to the mentioned
    // agent's pubsub token, so receiving it always means "I was mentioned" —
    // bump the sidebar badge regardless of which view is currently open.
    dispatch('conversationStats/incrementMentionCount', null, { root: true });
    // Flag it as unread so the row marker / Mentions unread filter pick it up
    // live (it's freshly mentioned and unopened).
    dispatch('conversationStats/addUnreadMention', conversation.id, {
      root: true,
    });
    if (isOnMentionsView(rootState)) {
      // Force-add: this conversation genuinely belongs on the mentions view,
      // so surface it even if it isn't in the store yet. Bypasses the
      // upsert guard in `updateConversation`.
      dispatch('commitConversation', conversation);
    }
  },

  addUnattended({ dispatch, rootState }, conversation) {
    if (isOnUnattendedView(rootState)) {
      dispatch('commitConversation', conversation);
    }
  },

  updateConversation({ dispatch, rootState, state }, conversation) {
    // Generic account-wide `conversation.updated` / `assignee.changed` /
    // `status_changed` / `read` events all flow here. On server-filtered
    // views (mentions / unattended / replied-by-me) and saved-folder views the
    // store is seeded only with the rows that belong to the view, so a generic
    // update must only *refresh* a row that's already shown — never inject a
    // new one. The UPDATE_CONVERSATION mutation upserts when the id is missing,
    // and an admin receives these events for the entire account, so without
    // this guard every account-wide update leaks a non-matching conversation
    // into the list. Genuine new matches arrive via the dedicated `addMentions`
    // / `addUnattended` paths or the folder-aware `addConversation` instead.
    const isFilteredView =
      isOnMentionsView(rootState) ||
      isOnUnattendedView(rootState) ||
      isOnRepliedByMeView(rootState) ||
      isOnFoldersView(rootState) ||
      isOnCommentsView(rootState);
    const isInStore = state.allConversations.some(
      c => c.id === conversation.id
    );
    if (isFilteredView && !isInStore) {
      return;
    }
    dispatch('commitConversation', conversation);
  },

  // Shared write path: commit the conversation and sync its labels + contact.
  // Used by the guarded `updateConversation` and by the view-specific
  // `addMentions` / `addUnattended` force-adds.
  commitConversation({ commit, dispatch }, conversation) {
    const {
      meta: { sender },
    } = conversation;
    commit(types.UPDATE_CONVERSATION, conversation);

    dispatch('conversationLabels/setConversationLabel', {
      id: conversation.id,
      data: conversation.labels,
    });

    dispatch('contacts/setContact', sender);
  },

  updateConversationLastActivity(
    { commit },
    { conversationId, lastActivityAt }
  ) {
    commit(types.UPDATE_CONVERSATION_LAST_ACTIVITY, {
      lastActivityAt,
      conversationId,
    });
  },

  setChatStatusFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_STATUS_FILTER, data);
  },

  setChatSortFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_SORT_FILTER, data);
  },

  updateAssignee({ commit }, data) {
    commit(types.UPDATE_ASSIGNEE, data);
  },

  updateConversationContact({ commit }, data) {
    if (data.id) {
      commit(`contacts/${types.SET_CONTACT_ITEM}`, data);
    }
    commit(types.UPDATE_CONVERSATION_CONTACT, data);
  },

  setActiveInbox({ commit }, inboxId) {
    commit(types.SET_ACTIVE_INBOX, inboxId);
  },

  muteConversation: async ({ commit }, conversationId) => {
    try {
      await ConversationApi.mute(conversationId);
      commit(types.MUTE_CONVERSATION);
    } catch (error) {
      //
    }
  },

  unmuteConversation: async ({ commit }, conversationId) => {
    try {
      await ConversationApi.unmute(conversationId);
      commit(types.UNMUTE_CONVERSATION);
    } catch (error) {
      //
    }
  },

  sendEmailTranscript: async (_, { conversationId, email }) => {
    try {
      await ConversationApi.sendEmailTranscript({ conversationId, email });
    } catch (error) {
      throw new Error(error);
    }
  },

  updateCustomAttributes: async (
    { commit },
    { conversationId, customAttributes }
  ) => {
    try {
      const response = await ConversationApi.updateCustomAttributes({
        conversationId,
        customAttributes,
      });
      const { custom_attributes } = response.data;
      commit(types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES, {
        conversationId,
        customAttributes: custom_attributes,
      });
    } catch (error) {
      // Handle error
    }
  },

  setConversationFilters({ commit }, data) {
    commit(types.SET_CONVERSATION_FILTERS, data);
  },

  clearConversationFilters({ commit }) {
    commit(types.CLEAR_CONVERSATION_FILTERS);
  },

  setChatListFilters({ commit }, data) {
    commit(types.SET_CHAT_LIST_FILTERS, data);
  },

  updateChatListFilters({ commit }, data) {
    commit(types.UPDATE_CHAT_LIST_FILTERS, data);
  },

  assignPriority: async ({ dispatch }, { conversationId, priority }) => {
    try {
      await ConversationApi.togglePriority({
        conversationId,
        priority,
      });

      dispatch('setCurrentChatPriority', {
        priority,
        conversationId,
      });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatPriority({ commit }, { priority, conversationId }) {
    commit(types.ASSIGN_PRIORITY, { priority, conversationId });
  },

  setContextMenuChatId({ commit }, chatId) {
    commit(types.SET_CONTEXT_MENU_CHAT_ID, chatId);
  },

  getInboxCaptainAssistantById: async ({ commit }, conversationId) => {
    try {
      const response = await ConversationApi.getInboxAssistant(conversationId);
      commit(types.SET_INBOX_CAPTAIN_ASSISTANT, response.data);
    } catch (error) {
      // Handle error
    }
  },

  ...messageReadActions,
  ...messageTranslateActions,
};

export default actions;
